import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input as ShadcnInput } from "@/components/ui/input";
import { FieldSelectFullWidth } from "@/components/trade-filter-selects";
import type { TradeAccountOption } from "../../contexts/ActiveBrokerAccountContext";

export const CSV_IMPORT_NEW_ACCOUNT_VALUE = "__new_account__";

const ADD_NEW_ACCOUNT_LABEL = "Add new account…";

function isCsvAccountIdentityField(field: string): boolean {
  const f = field.toLowerCase();
  return f === "account_code" || f === "account_name";
}

export { isCsvAccountIdentityField };

export interface CsvImportAccountCodeFieldProps {
  field: string;
  label: string;
  hint?: string | null;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  brokerAccounts: TradeAccountOption[];
  /** Start on “Add new account” instead of pre-selecting the first existing account (TradingView backtests). */
  preferNewAccountByDefault?: boolean;
  /** Shown under the picker when existing accounts are listed. */
  pickerHint?: string | null;
}

const CsvImportAccountCodeField = ({
  field,
  label,
  hint,
  placeholder,
  value,
  onChange,
  brokerAccounts,
  preferNewAccountByDefault = false,
  pickerHint,
}: CsvImportAccountCodeFieldProps): React.ReactElement => {
  const existingCodes = useMemo(
    () => Array.from(new Set(brokerAccounts.map((a) => a.accountCode).filter(Boolean))),
    [brokerAccounts]
  );

  const [pickerValue, setPickerValue] = useState<string>(CSV_IMPORT_NEW_ACCOUNT_VALUE);
  const preferNewAccountRef = useRef(preferNewAccountByDefault);
  const accountsKey = existingCodes.join("|");
  const pickerId = `csv-import-account-picker-${field}`;
  const inputId = `csv-import-account-input-${field}`;

  useEffect(() => {
    preferNewAccountRef.current = preferNewAccountByDefault;
    if (preferNewAccountByDefault) {
      setPickerValue(CSV_IMPORT_NEW_ACCOUNT_VALUE);
    }
  }, [accountsKey, field, preferNewAccountByDefault]);

  useEffect(() => {
    const trimmed = value.trim();

    if (trimmed && existingCodes.includes(trimmed)) {
      setPickerValue(trimmed);
      preferNewAccountRef.current = false;
      return;
    }

    if (trimmed) {
      setPickerValue(CSV_IMPORT_NEW_ACCOUNT_VALUE);
      preferNewAccountRef.current = true;
      return;
    }

    if (preferNewAccountRef.current) {
      setPickerValue(CSV_IMPORT_NEW_ACCOUNT_VALUE);
      return;
    }

    if (existingCodes.length > 0) {
      setPickerValue(existingCodes[0]);
      return;
    }

    setPickerValue(CSV_IMPORT_NEW_ACCOUNT_VALUE);
  }, [accountsKey, value, existingCodes]);

  const accountSelectOptions = useMemo(
    () => [
      { value: CSV_IMPORT_NEW_ACCOUNT_VALUE, label: ADD_NEW_ACCOUNT_LABEL },
      ...brokerAccounts.map((acc) => ({
        value: acc.accountCode,
        label: acc.tradeLabel || acc.accountCode,
      })),
    ],
    [brokerAccounts]
  );

  const showNewInput =
    pickerValue === CSV_IMPORT_NEW_ACCOUNT_VALUE || existingCodes.length === 0;

  const handlePickerChange = (next: string) => {
    setPickerValue(next);
    preferNewAccountRef.current = next === CSV_IMPORT_NEW_ACCOUNT_VALUE;
    if (next !== CSV_IMPORT_NEW_ACCOUNT_VALUE) {
      onChange(next);
    } else {
      onChange("");
    }
  };

  return (
    <Field className="gap-2" data-broker-csv-account-field="">
      <FieldLabel htmlFor={showNewInput ? inputId : pickerId} className={BROKER_ALERT_TITLE_CLASS}>
        {label}
        <span className="text-destructive"> *</span>
      </FieldLabel>
      {hint ? <FieldDescription className={BROKER_ALERT_DESC_CLASS}>{hint}</FieldDescription> : null}
      {existingCodes.length > 0 ? (
        <>
          <FieldSelectFullWidth
            id={pickerId}
            value={pickerValue}
            onValueChange={handlePickerChange}
            options={accountSelectOptions}
          />
          {pickerHint ? (
            <FieldDescription className={BROKER_ALERT_DESC_CLASS}>{pickerHint}</FieldDescription>
          ) : null}
        </>
      ) : null}
      {showNewInput ? (
        <ShadcnInput
          id={inputId}
          value={value}
          onChange={(e) => {
            preferNewAccountRef.current = true;
            onChange(e.target.value);
          }}
          placeholder={placeholder || `Enter ${label.toLowerCase()}`}
          className="font-[family-name:var(--font-primary)]"
        />
      ) : null}
    </Field>
  );
};

export default CsvImportAccountCodeField;
